import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Calendar, ChevronRight, X } from 'lucide-react';
import { getAgendaStatus, getDaysUntil, useAgendaStore } from '@/stores/agenda';
import { useFleetStore } from '@/stores/fleet';
import { cn } from '@/lib/utils';

const DISMISS_KEY = 'fismatek-agenda-banner-dismissed';

export function MaintenanceBanner() {
  const items = useAgendaStore((s) => s.items);
  const devices = useFleetStore((s) => s.devices);

  const alerts = useMemo(() => {
    const list = items
      .map((it) => ({ item: it, status: getAgendaStatus(it) }))
      .filter(({ status }) => status === 'overdue' || status === 'soon');
    return list.sort((a, b) => {
      if (a.status !== b.status) return a.status === 'overdue' ? -1 : 1;
      return a.item.dueDate.localeCompare(b.item.dueDate);
    });
  }, [items]);

  const overdueCount = alerts.filter((a) => a.status === 'overdue').length;
  const soonCount = alerts.filter((a) => a.status === 'soon').length;

  const stateKey = useMemo(
    () => alerts.map((a) => `${a.item.id}:${a.item.dueDate}`).join('|'),
    [alerts],
  );

  const [dismissedKey, setDismissedKey] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(DISMISS_KEY);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (dismissedKey && dismissedKey !== stateKey) {
      setDismissedKey(null);
      try {
        window.localStorage.removeItem(DISMISS_KEY);
      } catch {
        /* ignora */
      }
    }
  }, [stateKey, dismissedKey]);

  if (alerts.length === 0) return null;
  if (dismissedKey === stateKey) return null;

  const dismiss = () => {
    try {
      window.localStorage.setItem(DISMISS_KEY, stateKey);
    } catch {
      /* ignora */
    }
    setDismissedKey(stateKey);
  };

  const titleParts: string[] = [];
  if (overdueCount > 0) titleParts.push(`${overdueCount} vencida${overdueCount === 1 ? '' : 's'}`);
  if (soonCount > 0) titleParts.push(`${soonCount} próxima${soonCount === 1 ? '' : 's'}`);

  const hasOverdue = overdueCount > 0;

  return (
    <div
      className={cn(
        'flex items-center gap-3 border-b px-3 py-2 text-xs lg:px-4',
        hasOverdue
          ? 'border-status-blocked/30 bg-status-blocked/10 text-status-blocked'
          : 'border-status-ready/30 bg-status-ready/10 text-status-ready',
      )}
    >
      {hasOverdue ? (
        <AlertTriangle className="h-4 w-4 shrink-0" />
      ) : (
        <Calendar className="h-4 w-4 shrink-0" />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">Agenda: {titleParts.join(' · ')}</p>
        <p className="truncate text-[11px] opacity-80">
          {alerts
            .slice(0, 3)
            .map(({ item, status }) => {
              const device = item.deviceId != null ? devices.get(item.deviceId) : null;
              const who = device ? `${item.title} · ${device.name}` : item.title;
              const d = getDaysUntil(item.dueDate);
              const when =
                status === 'overdue'
                  ? `vencida há ${-d}d`
                  : d === 0
                    ? 'vence hoje'
                    : `vence em ${d}d`;
              return `${who} (${when})`;
            })
            .join(' · ')}
          {alerts.length > 3 && ` · +${alerts.length - 3}`}
        </p>
      </div>
      <Link
        to="/agenda"
        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-card px-3 py-1 text-[11px] font-semibold text-foreground ring-1 ring-border hover:bg-accent"
      >
        Ver agenda
        <ChevronRight className="h-3 w-3" />
      </Link>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 rounded-md p-1 opacity-70 hover:bg-card/50 hover:opacity-100"
        aria-label="Dispensar"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
